import { useApolloClient } from "@apollo/client";
import { useMemo } from "react";
import type { CompositionKey } from "skia-motion/src/CompositionsLight";
import { FlashingImagesAndWheelV1Props } from "skia-motion/src/compositions/FlashingImagesAndWheel/v1/types";
import GetShareableFlashingImagesPropsForGatheringDocument from "src/api/graphql/__generated__/documents/GetShareableFlashingImagesPropsForGatheringDocument";
import RequestGatheringShareableRecapBasicDocument from "src/api/graphql/__generated__/documents/RequestGatheringShareableRecapBasicDocument";
import ShareableVariant from "src/api/graphql/__generated__/enums/ShareableVariant";
import { GetShareableFlashingImagesPropsForGatheringQuery } from "src/api/graphql/__generated__/graphql";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import { useGatheringStore } from "src/contexts/GatheringContext";
import useClientProfile from "src/hooks/useClientProfile";
import useStabilizedCallback from "src/hooks/useStabilizedCallback";
import { useModal } from "src/modals";
import InternalModal, {
	ShareableGetDownloadUrl,
	ShareableGetProps,
	ShareableProps,
} from "src/modals/configs/InternalModal";
import ShareableNotEnoughPhotos from "../components/ShareableNotEnoughPhotos";

const processTopPhotos = (
	gatheringId: string,
	photos: NonNullable<
		GetShareableFlashingImagesPropsForGatheringQuery["getGatheringTopRelevantPhotos"]
	>,
) => {
	return photos
		.filter((photo) => photo.gatheringId === gatheringId && photo.readUrl?.previewUrl_9x16_md)
		.slice(0, 27);
};

type FlashingImagesAndWheelContext = {
	gatheringId: string;
	photos: {
		ownerId: string;
		photoId: string;
	}[];
	thumbnailV0TemplateUrl?: string;
};

const useGatheringShareable = () => {
	const name = useGatheringStore((state) => state.name);
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const numPhotos = useGatheringStore((state) => state.numPhotos);
	const apolloClient = useApolloClient();
	const { presentModal } = useModal<InternalModal>();
	const { profile } = useClientProfile();

	const handleGatheringRecapShare = useStabilizedCallback(() => {
		const composition = "FlashingImagesAndWheelV1" satisfies CompositionKey;
		const getProps: ShareableGetProps<
			typeof composition,
			FlashingImagesAndWheelContext
		> = async ({ apolloClient }) => {
			const {
				data: { getGathering, getGatheringTopRelevantPhotos },
			} = await apolloClient.query({
				query: GetShareableFlashingImagesPropsForGatheringDocument,
				variables: {
					topPhotosInput: { gatheringId },
					getGatheringInput: { gatheringId },
				},
				fetchPolicy: "network-only",
			});
			const thumbnailReadUrl = getGathering?.thumbnail?.readUrl;
			const photos = processTopPhotos(gatheringId, getGatheringTopRelevantPhotos ?? []);
			const photoUrls = photos
				.map((photo) => photo.readUrl?.previewUrl_9x16_md)
				.filter(Boolean) as string[];
			if (photoUrls.length === 0) {
				return {
					success: false,
					ErrorComponent: ShareableNotEnoughPhotos,
				};
			}

			return {
				success: true,
				props: {
					imageUrls: photoUrls,
					thumbnailUrl:
						parseGatheringThumbnailUrl({ readUrl: thumbnailReadUrl }) ?? undefined,
				} satisfies FlashingImagesAndWheelV1Props,
				context: {
					gatheringId,
					photos: photos.map((photo) => ({
						ownerId: photo.ownerId,
						photoId: photo.photoId,
					})),
					thumbnailV0TemplateUrl: thumbnailReadUrl?.v0TemplateUrl ?? undefined,
				} satisfies FlashingImagesAndWheelContext,
			};
		};
		const getDownloadUrl: ShareableGetDownloadUrl<
			typeof composition,
			FlashingImagesAndWheelContext
		> = async ({ apolloClient, context: { gatheringId, photos, thumbnailV0TemplateUrl } }) => {
			const { data } = await apolloClient.query({
				query: RequestGatheringShareableRecapBasicDocument,
				variables: {
					input: {
						gatheringId,
						photos,
						thumbnailV0TemplateUrl,
						variant: ShareableVariant.FlashingImagesAndWheelV1,
					},
				},
				fetchPolicy: "network-only",
				context: {
					pinBehavior: "long",
				},
			});
			const url = data?.requestGatheringShareableRecapBasic?.videoUrl?.url;
			if (!url) return null;

			return {
				type: "Video",
				url,
			};
		};

		presentModal("Shareable", {
			props: {
				shareable: {
					composition,
					initialProps: getProps({ apolloClient }),
					getProps,
					getDownloadUrl,
				} satisfies ShareableProps<typeof composition, FlashingImagesAndWheelContext>,
				title: profile?.firstName ? `${profile.firstName}'s recap` : "Your recap",
				subTitle: name ? `Here's your recap of ${name}` : undefined,
			},
			closeOnHookUnmount: false,
		});
	}, [apolloClient, gatheringId, name, presentModal, profile?.firstName]);

	const hasRecapShare = useMemo(() => (numPhotos ?? 0) > 0, [numPhotos]);

	return {
		hasRecapShare,
		handleGatheringRecapShare,
	};
};
export default useGatheringShareable;
